import React from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import SEO from '../components/SEO'
import './HowTo.css'

// The 1.0-5.0 vocabulary is shared with the landing page demo and the
// CreateScreen stage rail. Keep the numbering identical across all three.
const FORMATS = [
  { name: 'SOAP', letters: ['S', 'O', 'A', 'P'] },
  { name: 'CHART', letters: ['C', 'H', 'A', 'R', 'T'] },
  { name: 'DCHART', letters: ['D', 'C', 'H', 'A', 'R', 'T'] },
]

const HowTo = () => {
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin || {}

  return (
    <>
      <SEO
        title="How To Use PCRAssist - Step by Step Guide"
        description="Learn how to use PCRAssist effectively. Step-by-step instructions for creating, optimizing, and managing your PCR protocols with our AI-powered tools."
        canonicalUrl="/howto"
      />

      <div className="ht">
        <div className="ht__wrap">
          <header className="ht__head">
            <h1 className="ht__title">How to use this app</h1>
            <p className="ht__lede">
              A concise, step&#8209;by&#8209;step guide to creating and refining
              high&#8209;quality PCRs with PCRAssist.
            </p>
          </header>

          <div className="ht__notice">
            <p>
              You can use this app without registering, but only when you register (it&rsquo;s
              free!) and login will you be able to take advantage of advanced features like template
              merging, uploading your own protocol document, custom feedback checks, and report
              configuration.
            </p>
          </div>

          {/* ---- before you start ---------------------------------- */}
          <section className="ht__section" aria-labelledby="ht-start">
            <h2 id="ht-start">Before you start</h2>

            <div className="ht__block">
              <h3>How to register</h3>
              <p>
                Select <b>Sign in</b> in the top bar (or from the menu on a phone), then{' '}
                <b>Create an account</b>. The rest is straightforward.
              </p>
              <div className="ht__aside">
                <p>
                  Whether you are registered or not, none of your PCRs will be stored on our
                  systems. For more information on HIPAA related to this app, see the{' '}
                  <Link className="ht__link" to="/faq">
                    FAQ
                  </Link>
                  .
                </p>
              </div>
            </div>

            <div className="ht__block">
              <h3>Creating a new report</h3>
              <p>
                Click <b>New report</b> in the top bar. Choose the report type (SOAP, CHART, or
                DCHART). If logged in and you have templates defined, you can select one; otherwise,
                click <b>Start</b> to begin your PCR.
              </p>
            </div>
          </section>

          {/* ---- the five stages ----------------------------------- */}
          <section className="ht__section" aria-labelledby="ht-work">
            <h2 id="ht-work">Working on your PCR</h2>

            <ol className="ht__stages">
              <li className="ht__stage">
                <h3>
                  <span className="ht__n">1.0</span> Narrative
                </h3>
                <p>
                  You can type in full sentences or use shorthand (e.g., &ldquo;Unit 25 responded to
                  patient&rsquo;s residence, found patient seated in chair, 16g IV placed in left
                  antecubital, transported to ER&rdquo;). The system will automatically convert your
                  input into clear, professionally written English.
                </p>
                <p>
                  Prefer to speak instead? Click <b>Dictate</b> to start narrating. Click it again
                  when you&rsquo;re finished, and your spoken notes will be transcribed and added to
                  your narrative.
                </p>
                <div className="ht__aside">
                  <p>
                    You only need to include the relevant and unique details of the call&mdash;no
                    need to write out routine information.
                  </p>
                </div>
                <p>
                  In a later step, you can merge your narrative with a template that contains
                  standard statements for assessments or transports. When you&rsquo;re ready, click{' '}
                  <b>Next</b> to continue.
                </p>
              </li>

              <li className="ht__stage">
                <h3>
                  <span className="ht__n">2.0</span> Format
                </h3>
                <p>
                  After hitting <b>Next</b>, the initial report appears. You can copy/paste
                  sentences to different categories if needed. In the future, the system will adapt
                  to your style.
                </p>
                <p>
                  If you selected a template, the next step merges it into your narrative.
                  Otherwise, the next step creates a concise report.
                </p>
              </li>

              <li className="ht__stage">
                <h3>
                  <span className="ht__n">3.0</span> Template
                </h3>
                <p>
                  When logged in, you can define multiple templates for different call types (e.g.,
                  chest pain, trauma, behavioral emergencies). Templates can include frequently used
                  statements for assessments or transport summaries.
                </p>
                <div className="ht__aside">
                  <p>
                    <b>Example:</b> If your template says &ldquo;transport without incident&rdquo;
                    but your narrative says &ldquo;patient kicked an EMT&rdquo;, the final report
                    will mention the incident.
                  </p>
                </div>
              </li>

              <li className="ht__stage">
                <h3>
                  <span className="ht__n">4.0</span> Concise
                </h3>
                <p>
                  This step uses standard medical abbreviations and concise language to improve your
                  report.
                </p>
              </li>

              <li className="ht__stage">
                <h3>
                  <span className="ht__n">5.0</span> Review
                </h3>
                <p>
                  The final step analyzes your report for inconsistencies or missing information and
                  provides feedback you can use to update your report. You can define custom
                  feedback checks to ensure your report meets your organization&rsquo;s standards.
                </p>
              </li>
            </ol>

            <div className="ht__block">
              <h3>Going back to previous results</h3>
              <p>
                You can click on any step number above the report to return to previous
                results&mdash;no need to hit the back button multiple times.
              </p>
            </div>
          </section>

          {/* ---- custom feedback checks ---------------------------- */}
          <section className="ht__section" aria-labelledby="ht-checks">
            <h2 id="ht-checks">Configuring custom feedback checks</h2>
            <p>
              The{' '}
              <Link className="ht__link" to="/feedback">
                Feedback Checks
              </Link>{' '}
              screen helps you set up custom validation rules for different types of calls.
              Here&rsquo;s how to use it:
            </p>
            <ol className="ht__steps">
              <li>Navigate to the Feedback Checks screen</li>
              <li>
                Select an existing call type or click &ldquo;Add New Type&hellip;&rdquo; to create
                one
              </li>
              <li>Enter your custom checks in the text area</li>
              <li>Click Update to save your changes</li>
            </ol>
            <div className="ht__aside">
              <p>
                These checks will automatically run on your reports to ensure consistency and
                completeness, highlighting any missing or inconsistent information. You can define
                custom feedback checks to ensure your report meets your organization&rsquo;s
                standards.
              </p>
            </div>
          </section>

          {/* ---- report display ------------------------------------ */}
          <section className="ht__section" aria-labelledby="ht-display">
            <h2 id="ht-display">Customizing report display</h2>
            <p>
              Use the{' '}
              <Link className="ht__link" to="/report-config">
                Report Configuration
              </Link>{' '}
              screen to show or hide sections and set the exact display label for each one. For
              example, you can display <b>Subjective</b> as <b>&ldquo;S&rdquo;</b>,{' '}
              <b>&ldquo;Subjective&rdquo;</b>, <b>&ldquo;Subj&rdquo;</b>, or any label your agency
              prefers. Settings are saved per format.
            </p>

            {userInfo && (
              <>
                <div className="ht__formats">
                  {FORMATS.map((format) => (
                    <div className="ht__format" key={format.name}>
                      <span className="ht__format-name">{format.name}</span>
                      <div className="ht__chips">
                        {format.letters.map((letter, index) => (
                          <span className="ht__chip" key={`${format.name}-${index}`}>
                            {letter}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="ht__aside">
                  <p>
                    Tip: Edit the label text for each section in Report Configuration to match your
                    documentation style (e.g., &ldquo;S&rdquo; vs. &ldquo;Subjective&rdquo;).
                  </p>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </>
  )
}

export default HowTo
